import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useMeetingsAccess } from '../utils/meetingsAccess';
import { useCall } from '../context/CallContext';
import { useMeeting } from '../context/meetingContextCore';
import MeetingPreJoin from '../components/meeting/MeetingPreJoin';
import { dayText, timeText } from '../components/meeting/meetingHelpers';
import '../styles/meetings.css';

/**
 * /meetings/:id/room - the pre-join screen.
 *
 * The meeting itself is not drawn here: it is a window owned by the signed-in
 * shell (context/MeetingContext.js), so it stays up when the person moves to
 * another page. This page only gets them in - and, if they are already in this
 * meeting, brings its window back.
 *
 * A token is only asked for when the person clicks Join now, so one is never
 * minted for somebody who looks and goes back. Hidden like the rest of
 * Meetings: anyone the server does not admit goes back to the dashboard.
 */
const MeetingRoom = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enabled, loaded } = useMeetingsAccess();
    const callContext = useCall();
    const meetingContext = useMeeting();

    const chatCall = callContext?.call?.status;
    const onChatCall = Boolean(chatCall) && chatCall !== 'idle';

    const [meeting, setMeeting] = useState(null);
    const [problem, setProblem] = useState('');
    const [joining, setJoining] = useState(false);

    const inThisOne = Boolean(meetingContext?.inMeeting) && meetingContext.activeId === String(id);
    const inAnother = Boolean(meetingContext?.inMeeting) && meetingContext.activeId !== String(id);

    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        api.get(`/meetings/${id}`)
            .then((res) => { if (alive) setMeeting(res.data); })
            .catch(() => { if (alive) setProblem('We could not find that meeting.'); });
        return () => { alive = false; };
    }, [enabled, id]);

    const goBack = useCallback(() => navigate('/meetings'), [navigate]);

    // Already in this meeting (a reload of the link, or a second click): just show its window.
    useEffect(() => {
        if (inThisOne) {
            meetingContext.expand();
            navigate('/meetings', { replace: true });
        }
    }, [inThisOne, meetingContext, navigate]);

    const join = async (choices) => {
        setJoining(true);
        setProblem('');
        try {
            const res = await api.post(`/meetings/${id}/join`, { joinMethod: 'in-app' });
            await meetingContext.enter({ meeting, join: res.data, choices });
            // The meeting is now a window over whichever page this is.
            navigate('/meetings', { replace: true });
        } catch (err) {
            const data = err.response?.data;
            setProblem(data?.code === 'TOO_EARLY' && data.opensAt
                ? `The room opens at ${timeText(data.opensAt)} on ${dayText(data.opensAt, { day: 'numeric', month: 'short' })}.`
                : data?.message || 'Could not join the meeting.');
        } finally {
            setJoining(false);
        }
    };

    if (!loaded) return null;
    if (!enabled) return <Navigate to="/dashboard" replace />;

    return (
        <div className="mt-room-page">
            {problem && !meeting && (
                <div className="mt-center-card">
                    <h2>{problem}</h2>
                    <button type="button" className="mt-btn ghost" onClick={goBack}>Back to meetings</button>
                </div>
            )}

            {meeting && meeting.kind === 'event' && (
                <div className="mt-center-card">
                    <h2>{meeting.title} is an event, so there is no room to join.</h2>
                    <button type="button" className="mt-btn ghost" onClick={goBack}>Back to meetings</button>
                </div>
            )}

            {meeting && meeting.kind === 'meeting' && inAnother && (
                <div className="mt-center-card">
                    <h2>You are already in a meeting</h2>
                    <p className="mt-subtle">Leave it first, then join this one.</p>
                    <button type="button" className="mt-btn primary" onClick={() => meetingContext.expand()}>Go to that meeting</button>
                    <button type="button" className="mt-btn ghost" onClick={goBack}>Back to meetings</button>
                </div>
            )}

            {meeting && meeting.kind === 'meeting' && !inAnother && onChatCall && (
                <div className="mt-center-card">
                    <h2>You are on a call</h2>
                    <p className="mt-subtle">Hang up first, then join the meeting.</p>
                    <button type="button" className="mt-btn ghost" onClick={goBack}>Back to meetings</button>
                </div>
            )}

            {meeting && meeting.kind === 'meeting' && !inAnother && !onChatCall && !inThisOne && (
                <>
                    <MeetingPreJoin
                        meeting={{ title: meeting.title, organizer: meeting.organizer?.name }}
                        joining={joining}
                        onJoin={join}
                        onCancel={goBack}
                    />
                    {problem && <p className="mt-notice mt-prejoin-problem">{problem}</p>}
                </>
            )}
        </div>
    );
};

export default MeetingRoom;
